import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  PricingTier,
} from "types/graphql-schema";

import AskAI from "@apps/ask-ai/ask-ai";
import { ChatSessionContext } from "@apps/ask-ai/ask-ai-context";
import AssessmentRoleSidebar from "@apps/assessments/components/assessment/role-sidebar";
import ProfilePrivateNotes from "@apps/profile/profile-private-notes";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import AppError from "@components/error/error";
import Layout from "@components/layout/layout";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";

import { MeetingPageContext } from "../meeting-page-context";
import ActionItems from "./action-items";
import Feedbacks from "./feedbacks";
import Goals from "./goals";
import MeetingDetailsSidebar from "./meeting-details-sidebar";
import {
  MeetingSidebarTabEnum,
  meetingSidebarAriaLabel,
} from "./meeting-view-sidebar-constants";
import Recognitions from "./recognitions";

const MeetingViewSidebar = ({
  meeting,
  selectedTopicId,
  onClickTopic,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  selectedTopicId?: number;
  onClickTopic?: (topic: { id: number }) => void;
}) => {
  const currentUser = currentUserVar();
  const meetingGroup = meeting?.meetingGroup;
  const currentOrganization = currentOrganizationVar();
  const { activeSidebarTab, setActiveSidebarTab } =
    useContext(MeetingPageContext);
  if (activeSidebarTab === undefined || setActiveSidebarTab === undefined) {
    throw new Error(
      "activeSidebarTab && setActiveSidebarTab needs to be defined",
    );
  }

  const defaultAskAIChatSessionId = null;
  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    defaultAskAIChatSessionId,
  );
  const [startedChatSession, setStartedChatSession] = useState(false);

  const handleChangeActiveTab = (
    option: ToggleButtonGroupType<MeetingSidebarTabEnum>,
  ) => {
    if (option.value) setActiveSidebarTab(option.value);
  };

  useEffect(() => {
    // when changing meeting, we reset Ask AI
    setAskAIChatSessionId(defaultAskAIChatSessionId);
    setStartedChatSession(false);
  }, [meeting.id]);

  const participants = assertEdgesNonNullWithStringId(meeting.participants);
  const participantUsers = compact(participants.map(({ user }) => user));
  // const facilitator = meeting?.meetingGroup?.facilitator || participantUsers[0];
  const subject = participantUsers.find(
    ({ id }) => id === meeting?.subject?.id,
  );
  const oneOnOneSubject = meetingGroup?.isFormalOneonone ? subject : null;
  const isAdmin = isAdminVar();
  const currentUserIsManagerOfSubject =
    !!oneOnOneSubject &&
    (currentUser.directReports.edges || []).some(
      (dr) => dr?.node?.id === oneOnOneSubject.id,
    );
  const subjectIsCurrentUser = currentUser.id === oneOnOneSubject?.id;
  const isInReportingTree = !!meeting.subject?.isInCurrentUserReportingTree;
  const canViewRolesAndReviews =
    oneOnOneSubject && (isAdmin || isInReportingTree || subjectIsCurrentUser);

  const topics = assertEdgesNonNull(meeting.topics);

  const workHistoryStartDate = meeting.previousMeeting
    ? moment(meeting.previousMeeting.startDate)
    : moment().subtract(7, "days");

  // If the end date is in the future, clamp the end date to today
  const meetingTimeOrNow = meeting.startDate
    ? moment(meeting.startDate)
    : moment();
  const workHistoryEndDate = meetingTimeOrNow.isAfter(moment().startOf("day"))
    ? moment()
    : meetingTimeOrNow;
  const workHistoryStartDateIsInFuture = workHistoryStartDate
    .startOf("day")
    .isAfter(moment().startOf("day"));

  const showSidebarItemsTab =
    currentOrganization.featureFlags.actionItems ||
    currentOrganization.featureFlags.goals ||
    currentOrganization.featureFlags.recognitions ||
    currentOrganization.featureFlags.feedbacks;

  const tabs = compact([
    showSidebarItemsTab && {
      value: MeetingSidebarTabEnum.artifacts,
      title: MeetingSidebarTabEnum.artifacts,
      active: activeSidebarTab === MeetingSidebarTabEnum.artifacts,
    },
    currentOrganization.featureFlags.askAi &&
      currentOrganization.pricingTier === PricingTier.Ultimate && {
        value: MeetingSidebarTabEnum.askAI,
        title: MeetingSidebarTabEnum.askAI,
        active: activeSidebarTab === MeetingSidebarTabEnum.askAI,
      },
    {
      value: MeetingSidebarTabEnum.details,
      title: MeetingSidebarTabEnum.details,
      active: activeSidebarTab === MeetingSidebarTabEnum.details,
    },
    currentOrganization.featureFlags.workHistory &&
      meetingGroup?.isFormalOneonone && {
        value: MeetingSidebarTabEnum.workHistory,
        title: MeetingSidebarTabEnum.workHistory,
        active: activeSidebarTab === MeetingSidebarTabEnum.workHistory,
      },
    meetingGroup?.isFormalOneonone &&
      canViewRolesAndReviews && {
        value: MeetingSidebarTabEnum.rolesAndReviews,
        title: MeetingSidebarTabEnum.rolesAndReviews,
        active: activeSidebarTab === MeetingSidebarTabEnum.rolesAndReviews,
      },
    meetingGroup?.isFormalOneonone &&
      waffle.flag_is_active("private-notes") &&
      oneOnOneSubject &&
      (subjectIsCurrentUser || currentUserIsManagerOfSubject) && {
        value: MeetingSidebarTabEnum.myNotes,
        title: MeetingSidebarTabEnum.myNotes,
        active: activeSidebarTab === MeetingSidebarTabEnum.myNotes,
      },
  ]);

  useEffect(() => {
    if (tabs.length > 0) {
      const activeTab = tabs.find((tab) => tab.active);
      if (!activeTab) {
        setActiveSidebarTab(tabs[0].value);
      }
    }
  }, [tabs, setActiveSidebarTab]);

  if (!meetingGroup) return null;

  return (
    <Layout.Sidebar<MeetingSidebarTabEnum>
      tabs={tabs}
      fullHeight={activeSidebarTab === MeetingSidebarTabEnum.askAI}
      onChangeTab={handleChangeActiveTab}
      sidebarExpandedUiPreferenceKey="meetingSidebarExpanded"
      aria-label={meetingSidebarAriaLabel}
    >
      {activeSidebarTab === MeetingSidebarTabEnum.details && (
        <MeetingDetailsSidebar meeting={meeting} />
      )}
      {activeSidebarTab === MeetingSidebarTabEnum.topics && onClickTopic && (
        <Layout.SidebarSection>
          <div className="flex flex-col gap-2 text-sm font-medium text-gray-500">
            {topics.map((topic) => (
              <div key={topic.id}>
                <button
                  className={classNames(
                    "hover:text-gray-900 text-left",
                    selectedTopicId === topic.id && "text-gray-900 font-bold",
                  )}
                  onClick={() => onClickTopic(topic)}
                >
                  {topic.title}
                </button>
              </div>
            ))}
          </div>
        </Layout.SidebarSection>
      )}
      {activeSidebarTab === MeetingSidebarTabEnum.rolesAndReviews &&
        canViewRolesAndReviews && (
          <AssessmentRoleSidebar target={oneOnOneSubject} />
        )}

      {activeSidebarTab === MeetingSidebarTabEnum.artifacts &&
        currentOrganization.featureFlags.actionItems && (
          <ActionItems meeting={meeting} />
        )}

      {activeSidebarTab === MeetingSidebarTabEnum.artifacts &&
        currentOrganization.featureFlags.goals && <Goals meeting={meeting} />}

      {activeSidebarTab === MeetingSidebarTabEnum.artifacts &&
        currentOrganization.featureFlags.recognitions && (
          <Recognitions meeting={meeting} />
        )}

      {activeSidebarTab === MeetingSidebarTabEnum.artifacts &&
        currentOrganization.featureFlags.feedbacks &&
        meetingGroup.isFormalOneonone && <Feedbacks meeting={meeting} />}

      {activeSidebarTab === MeetingSidebarTabEnum.askAI && (
        <ChatSessionContext.Provider value={{ meetingId: meeting.id }}>
          <AskAI
            isInSidebar={true}
            chatSessionId={askAIChatSessionId}
            onChangeChatSessionId={setAskAIChatSessionId}
            isFullScreen={!!askAIChatSessionId || startedChatSession}
            className="px-6 pb-6"
            totalYPaddingAroundContainer={110}
            onStartChatSession={() => {
              setStartedChatSession(true);
            }}
          />
        </ChatSessionContext.Provider>
      )}

      {activeSidebarTab === MeetingSidebarTabEnum.myNotes &&
        oneOnOneSubject && (
          <div>
            <div className="flex text-gray-500 font-medium text-xs items-center gap-2 px-6 pt-4">
              <TopicflowIcon type="eyeOff" className="h-4 w-4" />
              Only visible to you
            </div>
            <ProfilePrivateNotes
              profileId={oneOnOneSubject.id}
              className={classNames(
                "px-6 py-4 flex-1 border-0",
                "min-h-[calc(100vh-240px)]",
              )}
            />
          </div>
        )}

      {activeSidebarTab === MeetingSidebarTabEnum.workHistory && (
        <>
          {workHistoryStartDateIsInFuture ? (
            <div className="p-4">
              This meeting is too far into the future to have a work history,
              please check back later!
            </div>
          ) : (
            meeting.subject && (
              <WorkHistorySidebar
                userId={meeting.subject.id}
                startDate={workHistoryStartDate.format("YYYY-MM-DD")}
                endDate={workHistoryEndDate.format("YYYY-MM-DD")}
                hasViewingPermission={subjectIsCurrentUser || isInReportingTree}
              />
            )
          )}
        </>
      )}
    </Layout.Sidebar>
  );
};

const MeetingViewSidebarWithSentry = withErrorBoundary(MeetingViewSidebar, {
  fallback: (
    <AppError description={"The meeting sidebar could not be displayed."} />
  ),
});

export default MeetingViewSidebarWithSentry;
